/**
 * The `source` discriminator on every postMessage between the loader (which runs on the
 * CUSTOMER's page) and the widget app (which runs in the iframe). Each side drops frames
 * whose source it does not recognise — that filter is what keeps the channel safe on a
 * hostile host page, and it is also why renaming these values is a PROTOCOL change.
 *
 * ── Why dual-accept, and why it is not optional ──────────────────────────────────────
 * The app is fetched from our origin, so it updates the instant we deploy. The loader is
 * embedded by a stable URL and served cacheable, so a visitor's browser can hold a
 * pre-rename copy long after that deploy — a CDN purge cannot reach it. "Old loader
 * talking to new app" is therefore the NORMAL state after every release, not a rollout
 * blip, and there is no deploy order that avoids it.
 *
 * So: emit the neutral value, accept both. Drop the legacy value only once loader caches
 * have demonstrably turned over — verify by serving a pre-rename loader against the
 * deployed app and confirming the widget still opens.
 */

/** Emitted on every app → loader message. */
export const APP_SOURCE = 'lc-widget';
/** Emitted on every loader → app message. */
export const LOADER_SOURCE = 'lc-loader';

/** Pre-rename values. ACCEPT-ONLY — never emit these. */
const LEGACY_APP_SOURCE = 'flozic-widget';
const LEGACY_LOADER_SOURCE = 'flozic-loader';

/** True for a frame originating from the widget app, current or pre-rename. */
export function isAppSource(source: unknown): boolean {
  return source === APP_SOURCE || source === LEGACY_APP_SOURCE;
}

/** True for a frame originating from the loader, current or pre-rename. */
export function isLoaderSource(source: unknown): boolean {
  return source === LOADER_SOURCE || source === LEGACY_LOADER_SOURCE;
}
